import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  HIT_BOARD_STATUS, HIT_LIST_BOARD, GET_LIST_MEMBER_BOARD, HIT_LIST_MEMBER_BOARD,
} from 'redux/actions/board';
import {
  DO_LOADING, DO_SWITCH_OFF, DO_SWITCH_ON, DO_TRIGGER,
} from 'redux/actions/loading';
import {
  apiCreateBoard,
  apiDeleteBoard,
  apiDuplicateBoard,
  apiInviteBoard,
  apiListMemberBoard,
  apiUpdateBoard,
  apiListBoard,
} from 'redux/api/board';
import { apiGetDashboard } from 'redux/api/dashboard';

const random = () => Math.random()?.toString(36).slice(-5);
const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export function useListBoardQuery(trigger?: unknown, enabled: boolean = true) {
  const dispatch = useDispatch();
  const memberId = localStorage.getItem('member_id');

  return useQuery({
    queryKey: ['board-list', trigger],
    queryFn: async () => {
      const response = await apiListBoard({ member_id: memberId });
      dispatch({
        type: HIT_LIST_BOARD,
        payload: { member_id: memberId },
      });
      return response;
    },
    enabled: Boolean(memberId) && enabled,
    meta: {
      onError: (error: unknown) => {
        dispatch({
          type: HIT_BOARD_STATUS,
          payload: { status: 'failed', message: getErrorMessage(error) },
        });
      },
    },
  });
}

export function useDashboardCountQuery() {
  const memberId = localStorage.getItem('member_id');

  return useQuery({
    queryKey: ['dashboard-count'],
    queryFn: () => apiGetDashboard({ member_id: memberId }),
    enabled: Boolean(memberId),
  });
}

export function useListMemberBoardQuery(boardId: string | undefined, trigger?: unknown, enabled: boolean = true) {
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ['board-member', boardId, trigger],
    queryFn: async () => {
      dispatch({ type: DO_SWITCH_ON, payload: 'memberBoard' });
      const response = await apiListMemberBoard({ board_id: boardId });
      dispatch({ type: GET_LIST_MEMBER_BOARD, payload: response });
      dispatch({ type: DO_SWITCH_OFF, payload: 'memberBoard' });
      dispatch({ type: DO_TRIGGER, payload: random() });
      return response;
    },
    enabled: Boolean(boardId) && enabled,
    meta: {
      onError: (error: unknown) => {
        dispatch({ type: DO_SWITCH_OFF, payload: 'memberBoard' });
        dispatch({ type: DO_TRIGGER, payload: random() });
        dispatch({
          type: HIT_BOARD_STATUS,
          payload: { status: 'failed', message: getErrorMessage(error) },
        });
      },
    },
  });
}

export function useAddBoardMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: apiCreateBoard,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (response) => {
      dispatch({
        type: HIT_LIST_BOARD,
        payload: { member_id: localStorage.getItem('member_id') },
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'success', message: response.message },
      });
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({ type: DO_TRIGGER, payload: random() });
    },
    onError: (error) => {
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'failed', message: getErrorMessage(error) },
      });
    },
  });
}

export function useUpdateBoardMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: apiUpdateBoard,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (response) => {
      dispatch({
        type: HIT_LIST_BOARD,
        payload: { member_id: localStorage.getItem('member_id') },
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'success', message: response.message },
      });
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({ type: DO_TRIGGER, payload: random() });
    },
    onError: (error) => {
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'failed', message: getErrorMessage(error) },
      });
    },
  });
}

export function useDuplicateBoardMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: apiDuplicateBoard,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (response) => {
      dispatch({
        type: HIT_LIST_BOARD,
        payload: { member_id: localStorage.getItem('member_id') },
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'success', message: response.message },
      });
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({ type: DO_TRIGGER, payload: random() });
    },
    onError: (error) => {
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'failed', message: getErrorMessage(error) },
      });
    },
  });
}

export function useDeleteBoardMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: apiDeleteBoard,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (response) => {
      dispatch({
        type: HIT_LIST_BOARD,
        payload: { member_id: localStorage.getItem('member_id') },
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'success', message: response.message },
      });
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({ type: DO_TRIGGER, payload: random() });
    },
    onError: (error) => {
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'failed', message: getErrorMessage(error) },
      });
    },
  });
}

export function useInviteBoardMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: apiInviteBoard,
    onMutate: () => {
      dispatch({ type: DO_LOADING, payload: true });
    },
    onSuccess: (response, variables: any) => {
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'success', message: response.message },
      });
      if (variables?.board_id) {
        dispatch({
          type: HIT_LIST_MEMBER_BOARD,
          payload: { board_id: variables.board_id },
        });
      }
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({ type: DO_TRIGGER, payload: random() });
    },
    onError: (error) => {
      dispatch({ type: DO_LOADING, payload: false });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: { status: 'failed', message: getErrorMessage(error) },
      });
    },
  });
}
